import React from 'react';
import { Link } from 'react-router-dom';
import { Cloud, CheckCircle, ArrowRight, Server, Shield, Zap, LineChart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { mockServices } from '../mock';
import SEO from '../components/SEO';

const CloudManaged = () => {
  const service = mockServices.find(s => s.slug === 'cloud-managed');

  const offerings = [
    {
      icon: Cloud,
      title: 'Infrastructure Solutions',
      description: 'End-to-end cloud native infrastructure solutions to help you start and optimize your cloud journey',
      link: '/services/infrastructure'
    },
    {
      icon: Server,
      title: 'DevOps Solutions',
      description: 'Streamlined build, test, and deploy workflows with DevOps practices tailored to your needs',
      link: '/services/devops'
    },
    {
      icon: LineChart,
      title: 'CloudOps (RaaS)',
      description: 'Reliability as a Service with real-time monitoring, proactive insights, and rapid incident resolution',
      link: '/services/cloudops'
    },
    {
      icon: Shield,
      title: 'Security & Compliance',
      description: 'Enterprise-grade security and compliance management for your cloud infrastructure',
      link: '/services/security'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Cloud Managed Services — Infra, DevOps & Security | NIVA Digital AI"
        description="Comprehensive cloud managed services including infrastructure solutions, DevOps automation, CloudOps RaaS, and security compliance. 99.9% uptime, HIPAA compliant."
        keywords="cloud managed services, DevOps automation, CloudOps RaaS, cloud infrastructure, cloud security, HIPAA compliant cloud"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/services/cloud-managed`}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-blue-50 via-sky-50 to-cyan-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: "url('https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1400')" }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/75 via-white/55 to-white/75" aria-hidden="true" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-sky-100 text-sky-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Cloud size={20} />
                <span>Cloud Managed Services</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Enterprise Cloud
                <span className="block text-red-600 mt-2">Management</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {service.shortDescription}. Optimize, secure, and scale your cloud infrastructure with confidence.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                  Get Started <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/17489157/pexels-photo-17489157.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Cloud data center with modern server racks"
                  className="w-full h-full object-cover"
                  data-testid="cloud-hero-image"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Offerings Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive Cloud Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From infrastructure to operations, we cover every aspect of cloud management
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {offerings.map((offering, index) => {
              const IconComponent = offering.icon;
              return (
                <Link key={index} to={offering.link} className="block">
                  <Card className="border-2 hover:border-red-600 transition-all duration-300 hover:shadow-xl cursor-pointer h-full">
                    <CardHeader>
                      <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="text-red-600" size={28} />
                      </div>
                      <CardTitle className="text-2xl mb-2 flex items-center justify-between">
                        {offering.title}
                        <ArrowRight className="text-red-600" size={20} />
                      </CardTitle>
                      <CardDescription className="text-base">{offering.description}</CardDescription>
                    </CardHeader>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-50 via-zinc-50 to-stone-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What We Deliver
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.features.map((feature, index) => (
              <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                    <p className="text-gray-700 font-medium">{feature}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Business Benefits
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Transform your cloud operations with measurable results
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="text-red-600" size={32} />
                </div>
                <p className="text-gray-700 font-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why Choose NIVA for Cloud Management?
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Expert Team</h3>
                    <p className="text-gray-600">Certified cloud architects and DevOps engineers with years of experience</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">24/7 Support</h3>
                    <p className="text-gray-600">Round-the-clock monitoring and support from our global delivery centers</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Cost Efficiency</h3>
                    <p className="text-gray-600">delivery model that provides enterprise expertise at competitive rates</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Proven Track Record</h3>
                    <p className="text-gray-600">500+ successful cloud migrations and infrastructure projects delivered</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-sky-100 to-sky-50 rounded-2xl p-12 shadow-2xl">
                <div className="space-y-6">
                  <div className="bg-white p-6 rounded-lg shadow">
                    <Shield className="text-sky-600 mb-2" size={32} />
                    <h4 className="font-semibold">Security First</h4>
                    <p className="text-sm text-gray-600 mt-1">Enterprise-grade security standards</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <Zap className="text-sky-600 mb-2" size={32} />
                    <h4 className="font-semibold">Fast Deployment</h4>
                    <p className="text-sm text-gray-600 mt-1">Rapid infrastructure scaling</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <LineChart className="text-sky-600 mb-2" size={32} />
                    <h4 className="font-semibold">Performance</h4>
                    <p className="text-sm text-gray-600 mt-1">Optimized for maximum uptime</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Cloud Services Use Cases
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real-world examples of cloud transformation success
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 hover:border-red-600 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-2xl mb-3 flex items-center">
                  <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center mr-4">
                    <Cloud className="text-sky-600" size={24} />
                  </div>
                  Legacy System Modernization
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  <strong>Challenge:</strong> Healthcare provider with legacy on-premise infrastructure needed cloud migration with zero downtime.
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Solution:</strong> Phased cloud migration using hybrid architecture, implementing Infrastructure as Code and automated backups.
                </p>
                <p className="text-gray-600 font-semibold text-red-600">
                  Result: 100% uptime during migration, 50% infrastructure cost reduction, improved scalability
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-red-600 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-2xl mb-3 flex items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                    <Server className="text-green-600" size={24} />
                  </div>
                  DevOps Transformation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  <strong>Challenge:</strong> SaaS company with manual deployments taking 2-3 days needed faster release cycles.
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Solution:</strong> Implemented CI/CD pipelines, containerization with Kubernetes, and automated testing frameworks.
                </p>
                <p className="text-gray-600 font-semibold text-red-600">
                  Result: Deploy time reduced from 3 days to 30 minutes, 80% fewer deployment failures
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-red-600 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-2xl mb-3 flex items-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                    <LineChart className="text-purple-600" size={24} />
                  </div>
                  24/7 CloudOps Support
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  <strong>Challenge:</strong> Growing startup experiencing frequent production incidents without dedicated ops team.
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Solution:</strong> Deployed CloudOps RaaS with 24/7 monitoring, automated incident response, and SLA-backed support.
                </p>
                <p className="text-gray-600 font-semibold text-red-600">
                  Result: 99.98% uptime achieved, mean time to resolution reduced by 75%
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-red-600 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-2xl mb-3 flex items-center">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                    <Shield className="text-red-600" size={24} />
                  </div>
                  Security & Compliance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  <strong>Challenge:</strong> Digital health platform needed HIPAA-compliant infrastructure with audit trails.
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Solution:</strong> Built secure cloud architecture with encryption, access controls, audit logging, and compliance monitoring.
                </p>
                <p className="text-gray-600 font-semibold text-red-600">
                  Result: HIPAA certified, passed all security audits, zero compliance violations
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Transform Your Cloud Infrastructure
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get a free cloud infrastructure assessment and consultation
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Schedule Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CloudManaged;